import { IStatHistory } from "@/data/api/main-data";
import { IUserStatHistory, IUserWorkerStat } from "@/data/api/monitoring-data";

export const SHARE_RATE_DECIMALS = 3;
const SHARE_RATE_SUFFIX = "share/s";

export function getNearestHourTimestamp() {
  const nearestHour = new Date();
  nearestHour.setUTCMinutes(0, 0, 0);
  return Math.floor(nearestHour.getTime() / 1000);
}

export function getNearestDayStart(date: number): number {
  const thatDay = new Date(date * 1000);
  thatDay.setUTCHours(0, 0, 0, 0);
  return Math.floor(thatDay.getTime() / 1000);
}

export function getCurrentTimeTimestamp() {
  return Math.floor(new Date().getTime() / 1000); //посмотреть как взять это значение по UTC
}

export function numberWithWhiteSpaces(x: number) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export function numberWithWhiteSpacesViaLocale(x: number) {
  const [integerPart, fractionalPart] = x.toString().split(".");

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  return fractionalPart
    ? `${formattedInteger}.${fractionalPart}`
    : formattedInteger;
}

export function getRandomIntFromInterval(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

export function getTimeFrom(startTime: number, period: number): number {
  if (period == 300) return startTime - 14_400; //5m
  if (period == 900) return startTime - 46_800; //15m
  if (period == 1800) return startTime - 93_600; //30m
  if (period == 3600) return startTime - 187_200; //1h
  if (period == 14400) return startTime - 748_800; //4h
  if (period == 86400) return startTime - 4_492_800; //d
  return startTime - 46_800;
}

export function getTimeFromUserPoolHistory(startTime: number): number {
  return startTime - 432_000;
}

export function convertHashrateToString(hashrate: number): string {
  if (hashrate == 0) return "0";
  const length = String(hashrate).length;
  const precision = getHashratePrecision(length);
  const suffix = getHashrateSuffix(precision);
  return (hashrate / Math.pow(10, precision)).toPrecision(5) + " " + suffix;
}

export function convertHashrateToFloatWithPrecision(
  value: number,
  precision: number
): number {
  if (value == 0) return 0;
  return Number(
    (value / Math.pow(10, precision)).toPrecision(String(value).length)
  );
}

export function getMaxHashrate(stats: IStatHistory[]): number {
  let max = 0;
  stats.forEach((element) => {
    if (max < element.power) max = element.power;
  });
  const length = String(max).length;
  return getHashratePrecision(length);
}

export function getMaxHashratePoolStats(
  stats: IStatHistory[],
  lastPower: number,
  pow: number
): number {
  let max = lastPower;
  stats.forEach((element) => {
    const realElementPower = element.power * Math.pow(10, pow);
    if (max < realElementPower) max = realElementPower;
  });
  const length = String(max).length;
  return getHashratePrecision(length);
}

export function getMaxHashrateUserStats(
  stats: IUserStatHistory[],
  lastPower: number,
  toPow: number
): number {
  let max = lastPower;
  stats.forEach((element) => {
    const powerInPow = element.power * Math.pow(10, toPow);
    if (max < powerInPow) max = powerInPow;
  });
  const length = String(max).length;
  return getHashratePrecision(length);
}

export function getMaxHashrateUserWorkerStats(
  stats: IUserWorkerStat[],
  toPow: number
): number {
  let max = 0;
  stats.forEach((element) => {
    if (max < element.power) max = element.power * Math.pow(10, toPow);
  });
  const length = String(max).length;
  return getHashratePrecision(length);
}

export function getHashrateSuffix(precision: number): string {
  if (precision == 24) return "YH/s";
  if (precision == 21) return "ZH/s";
  if (precision == 18) return "EH/s";
  if (precision == 15) return "PH/s";
  if (precision == 12) return "TH/s";
  if (precision == 9) return "GH/s";
  if (precision == 6) return "MH/s";
  if (precision == 3) return "KH/s";
  return "H/s";
}

function getHashratePrecision(value: number): number {
  if (value > 24) return 24;
  if (value == 24) return 21;
  if (value > 21) return 21;
  if (value == 21) return 18;
  if (value > 18) return 18;
  if (value == 18) return 15;
  if (value > 15) return 15;
  if (value == 15) return 12;
  if (value > 12) return 12;
  if (value == 12) return 9;
  if (value > 9) return 9;
  if (value == 9) return 6;
  if (value > 6) return 6;
  if (value == 6) return 3;
  if (value > 3) return 3;
  if (value == 3) return 0;
  return 0;
}

function parseShareRateNumber(value: unknown): number | undefined {
  if (typeof value == "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  const raw = String(value ?? "").trim();
  if (raw.length == 0) return undefined;

  const numberPart = raw.split(/\s+/)[0].replace(",", ".");
  const parsed = Number(numberPart);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function formatShareRateValue(
  value: unknown,
  decimals = SHARE_RATE_DECIMALS
): string {
  const parsed = parseShareRateNumber(value);
  if (parsed == undefined) {
    if (typeof value == "string" && value.trim().length > 0) return value.trim();
    return (0).toFixed(decimals);
  }
  return parsed.toFixed(decimals);
}

export function formatShareRate(
  value: unknown,
  decimals = SHARE_RATE_DECIMALS
): string {
  const parsed = parseShareRateNumber(value);
  if (parsed == undefined) {
    if (typeof value == "string" && value.trim().length > 0) return value.trim();
    return `${(0).toFixed(decimals)} ${SHARE_RATE_SUFFIX}`;
  }
  return `${parsed.toFixed(decimals)} ${SHARE_RATE_SUFFIX}`;
}
